import React, { useContext, useMemo } from "react";
import {Box, Icon, Skeleton, Table, TableCell, TableHeading, Text} from "@nike/eds";
import SnackbarContext from "../../providers/snackbar-context";
import "./EntryContextOverview.css";
import useSWR from "swr";
import DateTimeFormat from "../shared/DateTimeFormat";
import {Link} from "react-router-dom";

export function EntryContextOverview({ inventoryService, LocationTracker }) {
    const snackbarCtx = useContext(SnackbarContext);

    const fetcher = async () => {
        try {
            const result = await inventoryService.listFallbackEntryContexts();
            return result.data.entryContextFallbackSearch.items || [];
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "An unknown error occurred";
            snackbarCtx.displayMsg(errorMessage, "error");
            throw error;
        }
    };

    const { data: unsortedEntryContexts = [], isLoading } = useSWR(
        "entryContextFallbackSearch",
        fetcher,
        { refreshInterval: 60000 }
    );

    const entryContexts = useMemo(() => {
        return [...unsortedEntryContexts].sort((a, b) =>
            new Date(b.entryTimestamp).getTime() - new Date(a.entryTimestamp).getTime()
        );
    }, [unsortedEntryContexts]);

    const headers = [
        { Header: "Material" },
        { Header: "Size" },
        { Header: "MCO" },
        { Header: "Quality" },
        { Header: "Det. Unit Price (CRP) (USD)" },
        { Header: "Calculation Timestamp" },
        { Header: "Entry Trigger" },
        { Header: "Entry Timestamp" },
        { Header: "Link to Entry Event" }
    ];

    const triggerType = (type: string) => {
        switch (type) {
            case "NODE_INVENTORY_ADJUSTMENT":
                return "Node Inventory Adjustment";
            case "CUSTOMS_INVENTORY_ADJUSTMENT":
                return "Customs Inventory Adjustment";
            case "RETURN":
            case "UNEXPECTED_CARTON_RECEIPT":
            case "UNEXPECTED_DELIVERY_RECEIPT":
            case "DIVERT_CARTON_RECEIPT":
            case "DIVERT_DELIVERY_RECEIPT":
                return "Unexpected Receipt";
            default:
                return type;
        }
    };

    return (
        <div>
            <Text style={{ marginBottom: "1rem", fontWeight: "bold" }} font={"display-3"} as={"h3"}>
                Entry Context Corrections
            </Text>
            <Box className="eds-grid-ec eds-elevation--4">
                <Table>
                    <thead>
                    <tr>
                      <TableHeading colSpan={headers.length}> <Text font={"title-5"}> Determined Purchase Entry Context - Fallback </Text></TableHeading>
                    </tr>
                    <tr>
                        {headers.map((column) => (
                            <TableHeading key={column.Header}>{column.Header}</TableHeading>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {isLoading &&
                        <tr>
                            {headers.map((_, index) => (
                                <TableCell key={index}><Skeleton height={35} width={"100%"}/></TableCell>
                            ))}
                        </tr>
                    }
                    {!isLoading && !entryContexts?.length &&
                        <tr>
                            <TableCell colSpan={6}>No entry contexts found</TableCell>
                        </tr>
                    }
                    {!isLoading && entryContexts?.length > 0 &&
                        entryContexts.map((item) => (
                            <tr key={item.entryContextKey}>
                                <TableCell>{item.material}</TableCell>
                                <TableCell>{item.size}</TableCell>
                                <TableCell>{item.mco}</TableCell>
                                <TableCell>{item.quality}</TableCell>
                                <TableCell>{item.determinedUnitPrice}</TableCell>
                                <TableCell>
                                    <DateTimeFormat timestamp={item.calculationTimestamp} format="dd/MM/yyyy - HH:mm"/>
                                </TableCell>
                                <TableCell>{triggerType(item.entryTriggerType)}</TableCell>
                                <TableCell>
                                    <DateTimeFormat timestamp={item.entryTimestamp} format="dd/MM/yyyy - HH:mm"/>
                                </TableCell>
                                <TableCell>
                                    <Link to={"/flow-overview?flowId=" + item.flowId} reloadDocument>
                                        <Icon name="Show" size="m"/>
                                    </Link>
                                </TableCell>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Box>
            <LocationTracker location="Entry Context Overview" sublocation="Overview" />
        </div>
    );
}
